import { Component, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { RsfMainSBaseComponent } from '../rsf-main-s-base/rsf-main-s-base.component';
import { FormStatusEnum, Tag } from 'src/app/common/enumerations/enumerations';
import { SaveTaskResource, WFAction, WfSignature, WfSignatureResource } from 'src/app/components/workflow/workflow';
import { PrivilegeEnum } from 'src/app/common/enumerations/privilege-enum';
import { FormWorkflowComponent } from 'src/app/common/form-workflow/form-workflow.component';
import { OpApprovalValue } from 'src/app/controls/checklist-components/op-approval-w-checklists/op-approval-w-checklists.component';
import { SystemAffectedChanges } from '../rsf-main.component';

@Component({
  selector: 'rsf-main-s6',
  templateUrl: './rsf-main-s6.component.html',
  styleUrls: ['./rsf-main-s6.component.scss']
})
export class RsfMainS6Component extends RsfMainSBaseComponent implements OnChanges {

  @ViewChild(FormWorkflowComponent) formWorkflowComponent!: FormWorkflowComponent;

  override ngOnChanges(changes: SimpleChanges): void {
    if (!this.rsf) return;
    this.recomputeSignatures(); // Recompute signatures on any change to ensure permissions and disabled states are up to date.
  }

  /**
   * Recalculate signature permissions and checkbox states for section 6, then apply auto-check rules.
   * Restrict editability based on form status and caller privileges, and refresh the workflow UI afterward.
   * Mutate signature state in place and trigger workflow component refresh as a side effect.
   * @param change System-affected trigger used to scope downstream auto-check updates.
   */
  recomputeSignatures(change?: SystemAffectedChanges) {
    // Treat terminal and drafting-like statuses as non-editable for signature checkboxes.
    const disabledStatus = this.rsf?.status == FormStatusEnum.Closed ||
      this.rsf?.status == FormStatusEnum.Canceled ||
      this.rsf?.status == FormStatusEnum.AmendmentDrafting ||
      this.rsf?.status == FormStatusEnum.PendingCancelation;

    // Resolve section 6 signatures; fall back to an empty set when the workflow tree is missing.
    const signatures =
      this.rsf?.wfTable?.wfTableLocal?.wfSectionLocals
        ?.find(s => s.number === 6)
        ?.wfSignatures ?? [];

    // Compute per-signature privileges and effective checkbox disabled state.
    signatures.forEach(s => {
      const canChange = this.hasPrivilege(PrivilegeEnum.RSFOPApprovalCheckbox);
      const canChangeWoApproval = this.hasPrivilege(PrivilegeEnum.RSFOpAppWoApproval) && s.code == Tag.OP_APPR_RAD_CODE;
      s.privilegeToApproveChanges = this.privilegeToApproveChanges(s);
      s.checkboxDisabled = disabledStatus || !canChange && !canChangeWoApproval;
    });

    if (change)
      // Re-evaluate auto-checked values after permission and status recalculation.
      signatures.forEach(signature => this.autoCheckSignature(signature, change));


    // Force workflow UI to reflect updated signature flags.
    this.formWorkflowComponent?.refresh();
  }

  /**
   * Restrict the workflow-computed Close action until operational approvals are resolved and worker verification is signed.
   * Never enables Close beyond what the workflow allows, so the form cannot be closed before the Close step is active.
   * Applies visibility/disabled rules from task configuration and rebuilds workflow groups as a side effect.
   * @param signatures Workflow signatures used to compute close-action behavior.
   */
  signaturesCreated(signatures: WfSignature[]) {
    // Identify the signature tied to the Close workflow action.
    const closeSignature = signatures.find(s =>
      s?.wfTaskLocal?.wfActionLocals?.some(a => a.action === WFAction.Close)
    );

    // Exit when no close signature is configured for this workflow set.
    if (!closeSignature) return;

    // Detect unresolved operational approvals excluding the close signature itself.
    const hasPendingOps = signatures.some(s => {
      if (s?.id === closeSignature.id) return false;
      const valueObj = this.utils.JSONparse(s?.text);
      return !!valueObj?.checked && !s?.approved || s.pendingChange;
    });

    // Keep the workflow's own Close availability (active step, form status, role) so RSF cannot be closed
    // early, e.g. while PPS Testing Complete and PPS Return to Service are still unsigned.
    const workflowAllowsClose = !!closeSignature.approveAction;

    // Enable close approval only when the workflow allows it, no pending ops remain and worker verification is signed.
    closeSignature.approveAction = workflowAllowsClose && !hasPendingOps && !!this.rsf?.workerVerificationSigned;
    // Read UI behavior flags that control visibility/disabled fallback when close is not approvable.
    const configuration = this.utils.JSONparse(closeSignature.wfTaskLocal?.configuration);
    if (configuration) {
      if (!closeSignature.approveAction) {
        // Respect hideDisable policy when close action cannot be approved.
        closeSignature.visible = configuration.hideDisable ? true : false;
        closeSignature.disabled = !configuration.hideDisable ? false : true;
      }
      else {
        // Force visible and enabled when close action is approvable.
        closeSignature.visible = true;
        closeSignature.disabled = false;
      }

    }
    // Rebuild workflow groups so UI bindings reflect updated signature flags.
    this.formWorkflowComponent?.createGroups(signatures);
  }

  /**
   * Sync signature approval flags with shielding-modification inputs when related system changes occur.
   * Update and persist the signature only when the computed value differs, causing a local mutation on `signature.value`.
   * Preserve legacy ARC/BRC branching behavior used by this section.
   * @param signature Signature entry to evaluate and update in place.
   * @param change Triggering system-affected change used to scope auto-check updates.
   */
  autoCheckSignature(signature: WfSignatureResource, change?: SystemAffectedChanges) {

    const isAutoChecked = this.isAutoChecked(signature);

    // Route legacy auto-check behavior by signature tag.
    switch (signature.code) {
      // Keep ARC-specific shielding synchronization path for rollback compatibility.
      case Tag.ARC:
        if (!!this.rsf?.shieldingModificationARC != signature.value && (change === SystemAffectedChanges.ShModARC)) {
          // Normalize source value to boolean before mutating and persisting the signature.
          signature.value = !!this.rsf?.shieldingModificationARC;
          this.updateSignature(signature);
        }
        break;

      // Keep BRC-specific shielding synchronization path for rollback compatibility.
      case Tag.BRC:
        if (!!this.rsf?.shieldingModificationBRC != signature.value && (change === SystemAffectedChanges.ShModBRC)) {
          // Normalize source value to boolean before mutating and persisting the signature.
          signature.value = !!this.rsf?.shieldingModificationBRC;
          this.updateSignature(signature);
        }
        break;

      case Tag.OP_APPR_RAD_CODE:
      case Tag.DOC:
        if ((this.rsf?.shieldingModificationARC || this.rsf?.shieldingModificationBRC) != signature.value && (change === SystemAffectedChanges.ShModARC || change === SystemAffectedChanges.ShModBRC)) {
          // Auto-check if either shielding modification is enabled.
          signature.value = !!(this.rsf?.shieldingModificationARC || this.rsf?.shieldingModificationBRC);
          this.updateSignature(signature);
        }
        break;
    }
  }

  isAutoChecked(signature: WfSignatureResource): boolean {
    if (!signature.text) return true; // Default to autoChecked if no value is set yet
    const valueObj = this.utils.JSONparse(signature?.text);

    return valueObj?.autoChecked ?? true; // Default to true if autoChecked is not set
  }

  /**
   * Persist the current signature checkbox state as an auto-checked operational approval payload.
   * Update the in-memory signature text and submit the corresponding workflow task save request.
   * Mutate `signature.text` and trigger an async save side effect.
   * @param signature Signature entry whose value is serialized and persisted.
   */
  updateSignature(signature: WfSignatureResource) {
    // Build the normalized approval payload stored in signature text and save-task payload.
    const opApprovalValue = {
      checked: signature.value,
      autoChecked: true,
      user: -1,
    } as OpApprovalValue;

    // Keep local signature text synchronized with the serialized approval object.
    signature.text = this.utils.JSONstringify(opApprovalValue);
    // Prepare workflow task payload; clear text when the checkbox is not selected.
    const saveTask = {
      id: signature.wfTaskLocal?.id ?? 0,
      value: signature.value,
      text: signature.value ? this.utils.JSONstringify(opApprovalValue) : null
    } as SaveTaskResource;
    // Persist without blocking; subscriber is intentionally empty.
    this.service!.saveTask(saveTask, false).subscribe();

  }

  privilegeToApproveChanges(s: WfSignatureResource): PrivilegeEnum {
    const approveRads = s.code == Tag.OP_APPR_RAD_CODE && this.hasPrivilege(this.privilegeEnum.RSFApproveRADSChanges);
    const approveOther = s.code != Tag.OP_APPR_RAD_CODE && this.hasPrivilege(this.privilegeEnum.RSFOPApproval);
    if (approveRads) return PrivilegeEnum.SCFApproveRADSChanges;
    if (approveOther) return PrivilegeEnum.SCFOperationalApproval;
    return PrivilegeEnum.None;
  }
}
